import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  MoreVertical,
  UserMinus,
  Send,
  Clock,
  Crown,
  XCircle,
  Loader2,
  Eye,
  LogOut,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { CurrentUser, Member, PageInfo } from "./types";

interface MembersSectionProps {
  currentUser: CurrentUser;
  organizationName: string;
  members: Member[];
  orgLoading: boolean;
  membersPageInfo: PageInfo;
  loadingMoreMembers: boolean;
  handleLoadMoreMembers: () => Promise<void>;
  isAdmin: boolean;
  inviteEmail: string;
  setInviteEmail: (value: string) => void;
  invitingMember: boolean;
  handleInviteMember: () => void;
  handleKickMember: (memberId: string, memberName: string) => void;
  handleResendInvitation: (invitationId: string) => void;
  handleRevokeInvitation: (invitationId: string) => void;
}

export function MembersSection({
  currentUser,
  organizationName,
  members,
  orgLoading,
  membersPageInfo,
  loadingMoreMembers,
  handleLoadMoreMembers,
  isAdmin,
  inviteEmail,
  setInviteEmail,
  invitingMember,
  handleInviteMember,
  handleKickMember,
  handleResendInvitation,
  handleRevokeInvitation,
}: MembersSectionProps) {
  const inviterName = currentUser.name || currentUser.email || "A team member";
  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">Members</h2>
        <p className="text-muted-foreground text-lg">Manage your organization's team members</p>
      </div>

      <Separator />

      {/* Invite Form */}
      {isAdmin && (
        <Dialog>
          <div className="p-6 bg-muted/50 rounded-lg border border-border">
            <div className="space-y-3">
              <Label htmlFor="invite-email" className="text-base">Invite by Email</Label>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DialogTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-primary transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                      </DialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Preview invitation email</p>
                    </TooltipContent>
                  </Tooltip>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !invitingMember) {
                        handleInviteMember();
                      }
                    }}
                    disabled={invitingMember}
                    className="h-12 text-base"
                  />
                </div>
                <Button 
                  onClick={handleInviteMember} 
                  className="h-12"
                  disabled={invitingMember}
                >
                  {invitingMember ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Invite"
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Invitation Email Preview</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {/* Email Header */}
              <div className="space-y-1.5 pb-3 border-b border-border/50">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground font-medium w-16">From:</span>
                  <span>Flowlabs &lt;hello@flowlabs.live&gt;</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground font-medium w-16">To:</span>
                  <span className="text-primary">{inviteEmail || "recipient@example.com"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground font-medium w-16">Subject:</span>
                  <span className="font-medium">You've been invited to join {organizationName} on Flowlabs</span>
                </div>
              </div>
              
              {/* Email Body Preview */}
              <div className="space-y-3 text-sm">
                <h4 className="text-lg font-semibold">
                  You've been invited to join<br />{organizationName}
                </h4>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{inviterName}</span> has invited you to join their organization on Flowlabs as a <span className="font-medium text-foreground">Member</span>.
                </p>
                <p className="text-muted-foreground">
                  Click the button below to accept the invitation and get started.
                </p>
                <div className="pt-2">
                  <div className="inline-block bg-[#FF6633] text-white px-4 py-2 rounded-md text-sm font-medium">
                    Accept Invitation
                  </div>
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-md border-l-4 border-muted-foreground/30">
                  <p className="text-xs text-muted-foreground">
                    This invitation expires in 7 days
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Members List */}
      {orgLoading ? (
        <div className="text-center text-muted-foreground py-8 text-base">
          Loading members...
        </div>
      ) : members.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 text-base">
          No members found. Invite your first member to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((member) => {
            const isInvited = !!member.invitationId;
            const isCurrentUser = currentUser && member.id === currentUser.id;
            const isOwnerOrAdmin = member.role === "admin" || member.role === "ADMIN" || member.role === "OWNER";

            return (
              <div
                key={member.id}
                className={`flex items-center justify-between p-5 border border-border rounded-lg hover:bg-muted/50 transition-colors ${isInvited ? 'bg-muted/30' : ''}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-hero flex items-center justify-center text-white font-semibold text-lg">
                    {member.name?.charAt(0) || member.email?.charAt(0) || "?"}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-lg">{member.name || member.email}</p>
                      {isOwnerOrAdmin && (
                        <Crown className="w-4 h-4 text-primary" />
                      )}
                      {isInvited && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          Invited
                        </Badge>
                      )}
                    </div>
                    <p className="text-base text-muted-foreground">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge variant={isOwnerOrAdmin ? "default" : "secondary"}>
                    {member.role === "OWNER" ? "Owner" : member.role === "ADMIN" ? "Admin" : member.role === "admin" ? "Admin" : "Member"}
                  </Badge>
                  {/* Admin actions for other members */}
                  {isAdmin && !isCurrentUser && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isInvited && (
                          <>
                            <DropdownMenuItem onClick={() => handleResendInvitation(member.invitationId!)}>
                              <Send className="w-4 h-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRevokeInvitation(member.invitationId!)}
                              className="text-destructive"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Revoke Invitation
                            </DropdownMenuItem>
                          </>
                        )}
                        {!isInvited && (
                          <DropdownMenuItem
                            onClick={() => handleKickMember(member.id, member.name || member.email)}
                            className="text-destructive"
                          >
                            <UserMinus className="w-4 h-4 mr-2" />
                            Remove Member
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
          {membersPageInfo.hasNextPage && (
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={() => void handleLoadMoreMembers()}
                disabled={loadingMoreMembers}
                className="w-full sm:w-auto"
              >
                {loadingMoreMembers ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading more
                  </>
                ) : (
                  "Load more members"
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Leave org hint */}
      <div className="pt-4 border-t border-border/50">
        <Link 
          to="/settings?section=profile&tab=organizations"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          Want to leave this organization?
        </Link>
      </div>
    </div>
  );
}
